/**
 * Step 3 — 도메인 검증. FE-W-06.
 *
 * DNS TXT method:
 *   - store.dnsChallengeValue (예: "wcverif=abc123") 표시 + 복사 버튼
 *   - "검증 시도" → verifyAction({ method: 'dns_txt' })
 *
 * MX OTP method:
 *   - 첫 호출: verifyAction({ method: 'mx_otp' }) → otp_sent → OTP 입력 UI
 *   - 두 번째: verifyAction({ method: 'mx_otp', otp }) → 성공 또는 OTP_MISMATCH 카운터
 *
 * SOFT_DRIFT 응답: inline banner + 재시도 가능 (cookie 유지).
 * FINGERPRINT_MISMATCH: /onboarding/expired navigate.
 */

import { useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { OnboardingError, type VerifyMethod } from '../../../api/onboarding';
import { VerificationMethodSelector } from '../shared/VerificationMethodSelector';
import { tOnb, getOnboardingLang, subscribeOnboardingLang } from '../../../lib/onboarding-i18n';

export function Step3DomainVerifyPanel() {
  useSyncExternalStore(subscribeOnboardingLang, getOnboardingLang, getOnboardingLang);
  const navigate = useNavigate();
  const domain = useOnboardingStore((s) => s.domain);
  const dnsChallengeValue = useOnboardingStore((s) => s.dnsChallengeValue);
  const devBypassOtp = useOnboardingStore((s) => s.devBypassOtp);
  const verifyAction = useOnboardingStore((s) => s.verifyAction);
  const loading = useOnboardingStore((s) => s.loading);
  const clearError = useOnboardingStore((s) => s.clearError);

  const [method, setMethod] = useState<VerifyMethod>('dns_txt');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  // local error (server response 마지막 분기 — store.error 와 별개로 attempt 별 표시)
  const [localError, setLocalError] = useState<{
    code: string;
    message: string;
    matched?: number;
    total?: number;
    remainingAttempts?: number;
    retryable?: boolean;
  } | null>(null);

  async function copyChallenge() {
    if (!dnsChallengeValue) return;
    try {
      await navigator.clipboard.writeText(dnsChallengeValue);
      setCopiedAt(Date.now());
      setTimeout(() => setCopiedAt(null), 2_000);
    } catch {
      /* clipboard 권한 없음 — silent */
    }
  }

  async function doVerify(payload: { method: VerifyMethod; otp?: string }) {
    clearError();
    setLocalError(null);
    try {
      const r = await verifyAction(payload);
      if ('status' in r && r.status === 'otp_sent') {
        setOtpSent(true);
        return;
      }
      // success — currentStep=3 → navigate Step 4
      navigate('/onboarding/admin');
    } catch (err) {
      if (err instanceof OnboardingError) {
        if (err.payload.code === 'FINGERPRINT_MISMATCH') {
          navigate('/onboarding/expired', { replace: true });
          return;
        }
        setLocalError(err.payload);
      }
    }
  }

  function handleMethodChange(m: VerifyMethod) {
    setMethod(m);
    setOtp('');
    setOtpSent(false);
    setLocalError(null);
  }

  async function handleDnsVerify(e: FormEvent) {
    e.preventDefault();
    await doVerify({ method: 'dns_txt' });
  }

  async function handleMxRequest() {
    await doVerify({ method: 'mx_otp' });
  }

  async function handleMxVerify(e: FormEvent) {
    e.preventDefault();
    if (otp.length < 4) return;
    await doVerify({ method: 'mx_otp', otp });
  }

  return (
    <div>
      <h2 className="onboarding-step__heading">{tOnb('onboarding.step.label.verify')}</h2>
      <p className="onboarding-step__subheading">
        <code>{domain ?? tOnb('onboarding.step3.domain_unset')}</code>{tOnb('onboarding.step3.subheading_after_code')}
      </p>

      <VerificationMethodSelector method={method} onChange={handleMethodChange} disabled={loading} />

      {localError && localError.code === 'SOFT_DRIFT' && (
        <div className="onboarding-step__alert onboarding-step__alert--info" role="alert">
          {tOnb('onboarding.step3.soft_drift_msg')}
        </div>
      )}
      {localError && localError.code !== 'SOFT_DRIFT' && (
        <div className="onboarding-step__alert" role="alert">
          {localError.message}
          {typeof localError.matched === 'number' && typeof localError.total === 'number' && (
            <span style={{ display: 'block', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
              {localError.matched}/{localError.total} {tOnb('onboarding.step3.resolver_count_suffix')}
            </span>
          )}
          {typeof localError.remainingAttempts === 'number' && (
            <span style={{ display: 'block', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
              {tOnb('onboarding.step3.remaining_attempts', { n: localError.remainingAttempts })}
            </span>
          )}
        </div>
      )}

      {method === 'dns_txt' && (
        <form onSubmit={(e) => void handleDnsVerify(e)}>
          <div className="onboarding-step__field">
            <label className="onboarding-step__label">{tOnb('onboarding.step3.dns.label')}</label>
            <div className="onboarding-step__input-with-suffix" style={{ overflow: 'visible' }}>
              <input
                readOnly
                value={dnsChallengeValue ?? tOnb('onboarding.step3.dns.challenge_unset')}
                aria-label={tOnb('onboarding.step3.dns.aria_record')}
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.8125rem' }}
              />
              <button
                type="button"
                className="onboarding-step__suffix"
                onClick={() => void copyChallenge()}
                aria-label={tOnb('onboarding.step3.dns.copy_aria')}
                disabled={!dnsChallengeValue}
                style={{ cursor: 'pointer', border: 'none', background: 'var(--color-bg-elevated, #f3f4f6)' }}
              >
                {copiedAt ? tOnb('onboarding.step3.dns.copied') : tOnb('onboarding.step3.dns.copy')}
              </button>
            </div>
            <p className="onboarding-step__hint">
              {tOnb('onboarding.step3.dns.hint_host')}: <code>{domain}</code> · {tOnb('onboarding.step3.dns.hint_type')}: <code>TXT</code> · {tOnb('onboarding.step3.dns.hint_value')}: {tOnb('onboarding.step3.dns.hint_propagation')}
            </p>
          </div>

          <div className="onboarding-step__actions">
            <button
              type="button"
              className="onboarding-step__button onboarding-step__button--secondary"
              onClick={() => navigate('/onboarding/domain')}
            >
              {tOnb('onboarding.btn.prev')}
            </button>
            <button
              type="submit"
              className="onboarding-step__button onboarding-step__button--primary"
              disabled={loading || !dnsChallengeValue}
              aria-busy={loading}
            >
              {loading ? tOnb('onboarding.btn.verifying') : tOnb('onboarding.step3.dns.btn_verify')}
            </button>
          </div>
        </form>
      )}

      {method === 'mx_otp' && !otpSent && (
        <div>
          <p className="onboarding-step__hint">
            <code>postmaster@{domain}</code>{tOnb('onboarding.step3.mx.hint_send_after_code')}
          </p>
          <div className="onboarding-step__actions">
            <button
              type="button"
              className="onboarding-step__button onboarding-step__button--secondary"
              onClick={() => navigate('/onboarding/domain')}
            >
              {tOnb('onboarding.btn.prev')}
            </button>
            <button
              type="button"
              className="onboarding-step__button onboarding-step__button--primary"
              onClick={() => void handleMxRequest()}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? tOnb('onboarding.btn.sending') : tOnb('onboarding.step3.mx.btn_send')}
            </button>
          </div>
        </div>
      )}

      {method === 'mx_otp' && otpSent && (
        <form onSubmit={(e) => void handleMxVerify(e)}>
          <div className="onboarding-step__field">
            <label htmlFor="otp" className="onboarding-step__label">
              {tOnb('onboarding.step3.mx.label_otp')}
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              className="onboarding-step__input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
              required
              style={{ letterSpacing: '0.25em', fontVariantNumeric: 'tabular-nums' }}
            />
            {devBypassOtp && (
              <p className="onboarding-step__hint" style={{ color: 'orange' }}>
                {tOnb('onboarding.step3.mx.dev_bypass_prefix')}: <code>{devBypassOtp}</code>
              </p>
            )}
          </div>
          <div className="onboarding-step__actions">
            <button
              type="button"
              className="onboarding-step__button onboarding-step__button--secondary"
              onClick={() => {
                setOtpSent(false);
                setOtp('');
              }}
            >
              {tOnb('onboarding.step3.mx.btn_resend')}
            </button>
            <button
              type="submit"
              className="onboarding-step__button onboarding-step__button--primary"
              disabled={loading || otp.length !== 6}
              aria-busy={loading}
            >
              {loading ? tOnb('onboarding.btn.verifying') : tOnb('onboarding.step3.mx.btn_confirm')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
